/**
 * ComparisonsList Component
 *
 * Displays comparisons as cards with favorite star control.
 * Shows dynamic title, model pills, and created timestamp.
 *
 * Step 3 of ResultsDemo_BabyStep_Plan implementation
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { logger } from '../../../lib/logging';
import { EmptyState } from '../shared/States';
import type { Comparison, Response } from '../../../lib/types/results';
import type { ScoringResult } from '../../../services/comparison';
import type { DensityOption } from '../state/density';
import { getModelLogo } from '../../../utils/logoMapping';
import { fetchResponses } from '../../../lib/api/results';
import { formatModelDisplayName, inferProviderName } from '../../../utils/modelDisplayName';
import type { ModelMetadataEntry, ModelMetadataMap } from '../../../utils/modelDisplayName';
import { normalizeWinnerModelId } from '../../../utils/winnerNormalization';
// ModelFilter now rendered in ResultsSidebar

interface ModelBadgeInfo {
  modelId: string;
  displayName: string;
  providerName: string;
}

type ModelDetailsMap = ModelMetadataMap;

const createModelInfo = (
  modelId: string,
  detailsMap: ModelDetailsMap
): ModelBadgeInfo => ({
  modelId,
  displayName: formatModelDisplayName(modelId, detailsMap),
  providerName: inferProviderName(modelId, detailsMap)
});

const buildModelInfoFromIds = (
  ids: string[] | undefined,
  detailsMap: ModelDetailsMap
): ModelBadgeInfo[] => {
  if (!ids || ids.length === 0) {
    return [];
  }

  const uniqueIds = Array.from(new Set(ids));
  return uniqueIds.map(id => createModelInfo(id, detailsMap));
};

const buildModelInfoFromResponses = (
  comparisonResponses: Response[],
  detailsMap: ModelDetailsMap
): ModelBadgeInfo[] => {
  if (comparisonResponses.length === 0) {
    return [];
  }

  const unique = new Map<string, ModelBadgeInfo>();
  comparisonResponses.forEach(response => {
    if (!unique.has(response.modelId)) {
      unique.set(response.modelId, createModelInfo(response.modelId, detailsMap));
    }
  });

  return Array.from(unique.values());
};

const areModelListsEqual = (a?: ModelBadgeInfo[], b?: ModelBadgeInfo[]): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i += 1) {
    if (a[i].modelId !== b[i].modelId) {
      return false;
    }
  }

  return true;
};

// Legacy fallback functions removed - now only using composite scores for winner determination

const determineWinnerFromCompositeScores = (
  compositeScores: Array<{ modelId: string; compositeScore: number }>
): string | null => {
  if (compositeScores.length === 0) {
    return null;
  }

  // Find the model with the highest composite score
  const sorted = [...compositeScores].sort((a, b) => (b.compositeScore ?? -1) - (a.compositeScore ?? -1));

  // Only return winner if score is meaningful (> 0)
  // Score of null or 0 indicates no valid scoring data
  if (sorted[0].compositeScore == null || sorted[0].compositeScore <= 0) {
    return null;
  }

  return sorted[0].modelId;
};

const normalizeWinnerForComparison = (
  winnerId: string | null,
  comparison: Comparison | undefined
): string | null => {
  if (!winnerId) {
    return null;
  }

  if (!comparison) {
    return winnerId;
  }

  const { normalizedWinnerId } = normalizeWinnerModelId({
    winnerModelId: winnerId,
    requestedModelIds: comparison.modelsUsed
  });

  if (
    winnerId &&
    normalizedWinnerId &&
    winnerId !== normalizedWinnerId
  ) {
    logger.info('comparison_winner_id_normalized', undefined, {
      op: 'comparison_winner_id_normalized',
      component: 'ComparisonsList',
      comparisonId: comparison.id,
      backendWinnerId: winnerId,
      normalizedWinnerId,
      requestedModels: comparison.modelsUsed ?? [],
      providerAlias: normalizedWinnerId
    });
  }

  return normalizedWinnerId;
};

const prioritizeWinner = (
  models: ModelBadgeInfo[],
  winnerId: string | null
): ModelBadgeInfo[] => {
  if (!winnerId) {
    return models;
  }

  const winnerIndex = models.findIndex(model => model.modelId === winnerId);
  if (winnerIndex <= 0) {
    return models;
  }

  const winner = models[winnerIndex];
  return [winner, ...models.slice(0, winnerIndex), ...models.slice(winnerIndex + 1)];
};

/**
 * Get display title for a comparison card
 * Priority order:
 * 1. AI-generated description (from DeepSeek)
 * 2. Title (full original prompt, truncated for card display)
 * 3. Prompt (legacy fallback, truncated)
 */
const getDisplayTitle = (comparison: Comparison): string => {
  // Priority 1: Use AI-generated description
  const description = comparison.description?.trim();
  if (description && description.length > 0 && description !== 'Auto-generated comparison from POC flow') {
    return description;
  }

  // Priority 2: Use title (truncate if too long for card display)
  const title = comparison.title?.trim();
  if (title && title.length > 0) {
    return title.length > 100 ? title.substring(0, 100) + '...' : title;
  }

  // Priority 3: Fallback to prompt (legacy support)
  const prompt = comparison.prompt?.trim();
  if (prompt && prompt.length > 0) {
    const webSearchIndex = prompt.indexOf('## Web Search Context');
    if (webSearchIndex > 0) {
      const original = prompt.substring(0, webSearchIndex).trim();
      return original.length > 100 ? original.substring(0, 100) + '...' : original;
    }
    return prompt.length > 100 ? prompt.substring(0, 100) + '...' : prompt;
  }

  return 'Untitled Comparison';
};

export interface ComparisonsListProps {
  comparisons: Comparison[];
  responses: Response[];
  scoringResults?: ScoringResult[];
  modelDetails: Array<{ id: string; displayName: string; providerName: string }>;
  density?: DensityOption;
  onComparisonClick?: (comparison: Comparison) => void;
  onClearFilters?: () => void;
  favorites?: Set<string>;
  onFavoriteToggle?: (comparisonId: string) => void;
  isAnonymous?: boolean;
  // Model Filter props (passed from ResultsNewRoot)
  selectedModels?: Set<string>;
  filterMode?: 'any' | 'all';
  // Composite scores map (by comparison ID)
  compositeScoresMap?: Record<string, Array<{ modelId: string; compositeScore: number }>>;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

export const ComparisonsList: React.FC<ComparisonsListProps> = (props) => {
  const {
    comparisons,
    responses,
    scoringResults: _scoringResults,
    modelDetails,
    density = 'comfortable',
    onComparisonClick,
    onClearFilters,
    favorites: propFavorites,
    onFavoriteToggle,
    isAnonymous = false,
    selectedModels: propSelectedModels = new Set(),
    filterMode: propFilterMode = 'any',
    compositeScoresMap = {},
    hasMore = false,
    loadingMore = false,
    onLoadMore,
  } = props;

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // DEBUG: Log that ComparisonsList is rendering
  console.log('[ComparisonsList] RENDERING', {
    comparisonsCount: comparisons.length,
    responsesCount: responses.length,
    modelDetailsCount: modelDetails.length,
    density,
    selectedModelsCount: propSelectedModels.size,
    selectedModels: Array.from(propSelectedModels),
    filterMode: propFilterMode
  });

  // Preserve compatibility with callers that still supply scoring results
  void _scoringResults;
  // Use local state as fallback if no favorites prop provided
  const [localFavorites, setLocalFavorites] = useState<Set<string>>(new Set());
  const favorites = propFavorites ?? localFavorites;

  // Use filter state from props (now managed in ResultsNewRoot)
  const selectedModels = propSelectedModels;
  const filterMode = propFilterMode;

  const [comparisonModels, setComparisonModels] = useState<Record<string, ModelBadgeInfo[]>>({});
  const [winnersByComparison, setWinnersByComparison] = useState<Record<string, string | null>>({});
  const fetchedComparisonsRef = useRef<Set<string>>(new Set());
  const fetchInProgressRef = useRef<Set<string>>(new Set());

  const handleManualLoadMore = useCallback(() => {
    if (!onLoadMore || loadingMore || !hasMore) {
      return;
    }
    onLoadMore();
  }, [hasMore, loadingMore, onLoadMore]);

  useEffect(() => {
    if (!hasMore || !onLoadMore) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const target = sentinelRef.current;
    if (!target) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !loadingMore) {
          onLoadMore();
        }
      });
    }, {
      root: null,
      rootMargin: '0px 0px 200px 0px',
      threshold: 0.1
    });

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadingMore, onLoadMore]);


  // Filter state and URL management now handled in ResultsNewRoot

  // Pass 4: Filter comparisons based on selected models
  const filteredComparisons = useMemo(() => {
    console.log('[ComparisonsList] Computing filteredComparisons', {
      totalComparisons: comparisons.length,
      selectedModelsCount: selectedModels.size,
      selectedModels: Array.from(selectedModels),
      filterMode
    });

    // No filter applied - show all
    if (selectedModels.size === 0) {
      console.log('[ComparisonsList] No filter applied, showing all comparisons');
      return comparisons;
    }

    const filtered = comparisons.filter(comparison => {
      // Get models used in this comparison from responses
      const comparisonResponses = responses.filter(r => r.comparisonId === comparison.id);
      const comparisonModelIds = new Set(comparisonResponses.map(r => r.modelId));

      // Fallback to modelsUsed if no responses yet
      if (comparisonModelIds.size === 0 && comparison.modelsUsed) {
        comparison.modelsUsed.forEach(id => comparisonModelIds.add(id));
      }

      let matches = false;
      if (filterMode === 'any') {
        // ANY mode: Show if comparison has at least one selected model
        matches = Array.from(selectedModels).some(modelId => comparisonModelIds.has(modelId));
      } else {
        // ALL mode: Show if comparison has all selected models
        matches = Array.from(selectedModels).every(modelId => comparisonModelIds.has(modelId));
      }

      console.log('[ComparisonsList] Comparison filter check', {
        comparisonId: comparison.id,
        comparisonTitle: comparison.title?.substring(0, 50),
        comparisonModelIds: Array.from(comparisonModelIds),
        matches,
        filterMode
      });

      return matches;
    });

    console.log('[ComparisonsList] Filter result', {
      totalComparisons: comparisons.length,
      filteredComparisons: filtered.length,
      filterMode,
      selectedModels: Array.from(selectedModels)
    });

    return filtered;
  }, [comparisons, responses, selectedModels, filterMode]);

  // Inline editing removed from list view - edit only from detail view

  const modelDetailsMap = useMemo(() => {
    const map: ModelDetailsMap = new Map();
    modelDetails.forEach(detail => {
      const entry: ModelMetadataEntry = {
        displayName: detail.displayName,
        providerName: detail.providerName
      };
      map.set(detail.id, entry);
    });
    return map;
  }, [modelDetails]);

  const toggleFavorite = (comparisonId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click

    if (isAnonymous) {
      return;
    }

    if (onFavoriteToggle) {
      // Use parent's toggle handler if provided
      onFavoriteToggle(comparisonId);
      logger.info(favorites.has(comparisonId) ? 'comparison_unfavorited' : 'comparison_favorited', undefined, {
        op: favorites.has(comparisonId) ? 'comparison_unfavorited' : 'comparison_favorited',
        component: 'ComparisonsList',
        id: comparisonId
      });
    } else {
      // Fallback to local state management
      const newFavorites = new Set(localFavorites);
      if (localFavorites.has(comparisonId)) {
        newFavorites.delete(comparisonId);
        logger.info('comparison_unfavorited', undefined, {
          op: 'comparison_unfavorited',
          component: 'ComparisonsList',
          id: comparisonId
        });
      } else {
        newFavorites.add(comparisonId);
        logger.info('comparison_favorited', undefined, {
          op: 'comparison_favorited',
          component: 'ComparisonsList',
          id: comparisonId
        });
      }
      setLocalFavorites(newFavorites);
    }
  };

  // Edit handlers removed - edit only from detail view

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Sync comparison models from provided responses (e.g., selected comparison detail)
  useEffect(() => {
    if (responses.length === 0) {
      return;
    }

    const grouped = new Map<string, Response[]>();
    responses.forEach(response => {
      if (!grouped.has(response.comparisonId)) {
        grouped.set(response.comparisonId, []);
      }
      grouped.get(response.comparisonId)!.push(response);
    });

    const updates: Array<{ id: string; models: ModelBadgeInfo[]; winner: string | null }> = [];
    grouped.forEach((compResponses, compId) => {
      // Find the comparison to get modelsUsed
      const comp = comparisons.find(c => c.id === compId);

      if (comp?.modelsUsed) {
        // Start with requested models, enrich with response data
        const requestedModels = buildModelInfoFromIds(comp.modelsUsed, modelDetailsMap);
        const enrichedModels = requestedModels.map(model => {
          const response = compResponses.find(r => r.modelId === model.modelId);
          if (response?.model) {
            return {
              ...model,
              displayName: response.model.displayName || model.displayName,
              providerName: response.provider?.name || model.providerName
            };
          }
          return model;
        });
        // Only determine winner if composite scores are available (no fallback)
        const compositeScores = compositeScoresMap[compId];
        const winner = (compositeScores && compositeScores.length > 0)
          ? determineWinnerFromCompositeScores(compositeScores)
          : null;
        const normalizedWinner = normalizeWinnerForComparison(winner, comp);
        updates.push({ id: compId, models: enrichedModels, winner: normalizedWinner });
      } else {
        // Fallback to old behavior if modelsUsed not available
        const models = buildModelInfoFromResponses(compResponses, modelDetailsMap);
        // Only determine winner if composite scores are available (no fallback)
        const compositeScores = compositeScoresMap[compId];
        const winner = (compositeScores && compositeScores.length > 0)
          ? determineWinnerFromCompositeScores(compositeScores)
          : null;
        const normalizedWinner = normalizeWinnerForComparison(winner, comp);
        updates.push({ id: compId, models, winner: normalizedWinner });
      }
    });

    if (updates.length === 0) {
      return;
    }

    setComparisonModels(prev => {
      let changed = false;
      const next = { ...prev };

      updates.forEach(({ id, models }) => {
        if (models.length === 0) {
          return;
        }

        if (!areModelListsEqual(next[id], models)) {
          next[id] = models;
          changed = true;
        }
      });

      return changed ? next : prev;
    });

    setWinnersByComparison(prev => {
      let changed = false;
      const next = { ...prev };

      updates.forEach(({ id, winner }) => {
        const normalizedWinner = winner ?? null;
        if (next[id] !== normalizedWinner) {
          next[id] = normalizedWinner;
          changed = true;
        }
      });

      return changed ? next : prev;
    });

    updates.forEach(({ id }) => {
      fetchedComparisonsRef.current.add(id);
    });
  }, [responses, modelDetailsMap, compositeScoresMap]);

  // Fetch model and winner data for comparisons that haven't been hydrated yet
  useEffect(() => {
    if (comparisons.length === 0) {
      return;
    }

    const comparisonsToFetch = comparisons.filter(comparison =>
      !fetchedComparisonsRef.current.has(comparison.id) &&
      !fetchInProgressRef.current.has(comparison.id)
    );

    if (comparisonsToFetch.length === 0) {
      return;
    }

    let isActive = true;

    comparisonsToFetch.forEach(comparison => {
      fetchInProgressRef.current.add(comparison.id);

      const fallbackModels = buildModelInfoFromIds(comparison.modelsUsed, modelDetailsMap);

      if (fallbackModels.length > 0) {
        setComparisonModels(prev => {
          if (prev[comparison.id]) {
            return prev;
          }
          return { ...prev, [comparison.id]: fallbackModels };
        });

        // Don't set winner until composite scores are loaded
      }

      const loadComparisonData = async () => {
        try {
          const data = await fetchResponses(comparison.id, {
            bypassCache: true,
            correlationId: `comparison_card_${comparison.id}`
          });
          if (!isActive) {
            return;
          }

          const comparisonResponses: Response[] = Array.isArray((data as any)?.responses)
            ? (data as any).responses
            : [];

          if (comparisonResponses.length > 0) {
            // Start with all requested models to preserve those without responses
            const requestedModels = buildModelInfoFromIds(comparison.modelsUsed, modelDetailsMap);

            // Enrich with actual response data where available
            const enrichedModels = requestedModels.map(model => {
              const response = comparisonResponses.find(r => r.modelId === model.modelId);
              if (response?.model) {
                return {
                  ...model,
                  displayName: response.model.displayName || model.displayName,
                  providerName: response.provider?.name || model.providerName
                };
              }
              return model;
            });

            setComparisonModels(prev => {
              if (areModelListsEqual(prev[comparison.id], enrichedModels)) {
                return prev;
              }
              return { ...prev, [comparison.id]: enrichedModels };
            });

            // Only determine winner if composite scores are available (no fallback)
            const compositeScores = compositeScoresMap[comparison.id];
            const winnerId = (compositeScores && compositeScores.length > 0)
              ? determineWinnerFromCompositeScores(compositeScores)
              : null;
            const normalizedWinnerId = normalizeWinnerForComparison(winnerId, comparison);
            setWinnersByComparison(prev => {
              const normalizedWinner = normalizedWinnerId ?? null;
              if (prev[comparison.id] === normalizedWinner) {
                return prev;
              }
              return { ...prev, [comparison.id]: normalizedWinner };
            });
          }
        } catch (error) {
          if (!isActive) {
            return;
          }

          logger.error('comparison_models_fetch_error', undefined, {
            op: 'comparison_models_fetch_error',
            component: 'ComparisonsList',
            comparisonId: comparison.id,
            error: error instanceof Error ? error.message : 'Unknown error'
          });

          if (fallbackModels.length > 0) {
            setComparisonModels(prev => {
              if (areModelListsEqual(prev[comparison.id], fallbackModels)) {
                return prev;
              }
              return { ...prev, [comparison.id]: fallbackModels };
            });

            // Don't set winner until composite scores are loaded
          }
        } finally {
          fetchInProgressRef.current.delete(comparison.id);
          fetchedComparisonsRef.current.add(comparison.id);
        }
      };

      void loadComparisonData();
    });

    return () => {
      isActive = false;
    };
  }, [comparisons, modelDetailsMap, compositeScoresMap]);

  if (comparisons.length === 0) {
    return <EmptyState view="comparisons" onClearFilters={onClearFilters} isNewUser={true} />;
  }

  // Check if filtered to empty (comparisons exist but none match filters)
  if (filteredComparisons.length === 0) {
    return <EmptyState view="comparisons" onClearFilters={onClearFilters} isNewUser={false} />;
  }

  const densityGridClasses = {
    comfortable: 'grid-cols-1 md:grid-cols-2 gap-4',
    compact: 'grid-cols-1 md:grid-cols-3 gap-3',
    stacked: 'grid-cols-1 gap-4'
  };

  const isGridDensity = density !== 'stacked';

  const scoreChipClasses = [
    'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full',
    'border border-slate-500/60 bg-slate-900/70 text-sm font-semibold text-slate-200',
    'tracking-tight shadow-inner shadow-black/5'
  ].join(' ');

  // ModelFilter now rendered in ResultsSidebar, not here

  return (
    <div>

      <div className={`grid ${densityGridClasses[density]}`}>
      {filteredComparisons.map(comparison => {
        const isFavorited = favorites.has(comparison.id);
        const models = comparisonModels[comparison.id] ?? buildModelInfoFromIds(comparison.modelsUsed, modelDetailsMap);
        const winnerId = winnersByComparison[comparison.id] ?? null;
        const orderedModels = prioritizeWinner(models, winnerId);

        const statusMeta = (() => {
          switch (comparison.status) {
            case 'completed':
              return { color: 'bg-green-400', label: 'Status: Completed' } as const;
            case 'running':
              return { color: 'bg-yellow-400', label: 'Status: Processing' } as const;
            case 'queued':
            case 'pending':
              return { color: 'bg-yellow-300', label: 'Status: Pending' } as const;
            case 'failed':
            case 'error':
              return { color: 'bg-red-400', label: 'Status: Failed' } as const;
            case 'canceled':
            case 'cancelled':
              return { color: 'bg-slate-400', label: 'Status: Canceled' } as const;
            default:
              return { color: 'bg-slate-400', label: 'Status: Unknown' } as const;
          }
        })();

        const badgeGapClass = density === 'compact' ? 'gap-1' : 'gap-1.5';
        const winnerBadgeClasses = 'bg-yellow-500/10 text-yellow-200 border border-yellow-300/40 ring-1 ring-yellow-200/30 shadow shadow-yellow-300/20';
        const standardBadgeClasses = 'bg-cyan-500/10 text-cyan-300 border border-cyan-400/30';

        // Only show score if composite scores exist for this comparison (no fallbacks to legacy scores)
        const compositeScoresForComparison = compositeScoresMap[comparison.id];
        const maxScore = (compositeScoresForComparison && compositeScoresForComparison.length > 0)
          ? Math.max(...compositeScoresForComparison.map(s => s.compositeScore ?? 0))
          : 0;

        // Only display score if it's meaningful (> 0)
        // Score of 0 indicates no valid scoring data
        const displayScore = maxScore > 0 ? maxScore : null;
        const formattedScore = displayScore != null ? displayScore.toFixed(1) : null;

        return (
          <div
            key={comparison.id}
            onClick={() => onComparisonClick?.(comparison)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onComparisonClick?.(comparison);
              }
            }}
            tabIndex={0}
            role="button"
            aria-label={`View details for ${getDisplayTitle(comparison)}`}
            className="group relative flex h-full flex-col bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-5 cursor-pointer transition-all duration-200 hover:bg-slate-800/50 hover:border-cyan-500/30 hover:shadow-lg hover:shadow-cyan-500/10 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900"
          >
            {/* Header Row with Title + Actions */}
            <div className="flex items-start justify-between gap-4 mb-1.5">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {formattedScore != null && (
                  <div className={scoreChipClasses} aria-label={`Overall score ${formattedScore}`}>
                    <span className="text-sm font-semibold text-slate-100">
                      {formattedScore}
                    </span>
                  </div>
                )}
                <h3 className="text-lg font-semibold text-white line-clamp-2 group-hover:text-cyan-300 transition-colors flex-1 min-w-0">
                  {getDisplayTitle(comparison)}
                </h3>
              </div>
              {!isAnonymous && (
                <button
                  onClick={(e) => toggleFavorite(comparison.id, e)}
                  className="p-1.5 rounded-lg transition-all duration-200 hover:bg-slate-700/50"
                  aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <svg
                    className={`w-5 h-5 transition-colors ${
                      isFavorited
                        ? 'fill-yellow-400 stroke-yellow-400'
                        : 'fill-none stroke-slate-500 group-hover:stroke-slate-400'
                    }`}
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Badge + Timestamp Container - Stacked: same row with justify-between, Grid/Split: badges above metadata */}
            {density === 'stacked' ? (
              <div className="flex items-stretch justify-between gap-4">
                {/* Model badges */}
                <div className={`flex flex-wrap ${badgeGapClass} flex-1 min-w-0`}>
                  {orderedModels.map((model) => {
                    const isWinner = winnerId ? model.modelId === winnerId : false;
                    return (
                      <span
                        key={model.modelId}
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium transition-all ${
                          isWinner ? winnerBadgeClasses : standardBadgeClasses
                        } max-w-full min-w-0 overflow-hidden`}
                        title={isWinner ? `${model.providerName} - Winner` : model.providerName}
                        aria-label={isWinner ? `${model.displayName}, winner of this comparison` : model.displayName}
                      >
                        <img
                          src={getModelLogo(model.modelId, model.providerName)}
                          alt={`${model.displayName} logo`}
                          className="w-4 h-4 object-contain flex-shrink-0"
                        />
                        <span className="truncate">{model.displayName}</span>
                      </span>
                    );
                  })}
                </div>

                {/* Timestamp + Status - Stacked: right side of badge row */}
                <div className="flex items-center gap-2 text-xs text-slate-500 flex-shrink-0 self-end text-right">
                  <div className="flex items-center gap-1.5">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{formatDate(comparison.createdAt)}</span>
                  </div>
                  <div className="flex items-center">
                    <span
                      aria-hidden="true"
                      className={`inline-block w-2 h-2 rounded-full ${statusMeta.color}`}
                    />
                    <span className="sr-only">{statusMeta.label}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className={`flex flex-col gap-2 mt-2 ${isGridDensity ? 'md:flex-1' : ''}`}>
                {/* Model badges */}
                <div className={`flex flex-wrap ${badgeGapClass}`}>
                  {orderedModels.map((model) => {
                    const isWinner = winnerId ? model.modelId === winnerId : false;
                    return (
                      <span
                        key={model.modelId}
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium transition-all ${
                          isWinner ? winnerBadgeClasses : standardBadgeClasses
                        } max-w-full min-w-0 overflow-hidden`}
                        title={isWinner ? `${model.providerName} - Winner` : model.providerName}
                        aria-label={isWinner ? `${model.displayName}, winner of this comparison` : model.displayName}
                      >
                        <img
                          src={getModelLogo(model.modelId, model.providerName)}
                          alt={`${model.displayName} logo`}
                          className="w-4 h-4 object-contain flex-shrink-0"
                        />
                        <span className="truncate">{model.displayName}</span>
                      </span>
                    );
                  })}
                </div>

                {/* Timestamp + Status - Grid/Split: dedicated row under badges */}
                <div
                  className={`flex items-center justify-end gap-2 text-xs text-slate-500 ${
                    isGridDensity ? 'md:mt-auto md:self-end md:text-right' : ''
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{formatDate(comparison.createdAt)}</span>
                  </div>
                  <div className="flex items-center">
                    <span
                      aria-hidden="true"
                      className={`inline-block w-2 h-2 rounded-full ${statusMeta.color}`}
                    />
                    <span className="sr-only">{statusMeta.label}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      </div>

      {(hasMore || loadingMore) && (
        <div className="mt-6 flex flex-col items-center gap-3 text-sm text-slate-400">
          <button
            type="button"
            onClick={handleManualLoadMore}
            disabled={!hasMore || loadingMore}
            className={`px-4 py-2 rounded-lg border transition-colors ${
              loadingMore
                ? 'border-slate-600 bg-slate-800/50 text-slate-300 cursor-default'
                : 'border-slate-600 bg-slate-900/60 hover:bg-slate-800/60 text-slate-200'
            }`}
          >
            {loadingMore ? 'Loading more comparisons…' : 'Load more comparisons'}
          </button>
          <div
            ref={sentinelRef}
            aria-hidden="true"
            className="w-full h-1"
          />
          {!hasMore && loadingMore === false && (
            <p className="text-slate-500 text-xs">All comparisons loaded</p>
          )}
        </div>
      )}
    </div>
  );
};
